// CollapsibleSection.jsx - Collapsible section component for progressive disclosure
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';

/**
 * CollapsibleSection - Reusable collapsible section component
 * Shows/hides content with smooth transitions and item count badges
 */
const CollapsibleSection = ({
  title,
  count = 0,
  expanded = false,
  onToggle,
  children,
  preview = null, // Optional preview content to show when collapsed
  sx = {},
}) => {
  const theme = useTheme();
  
  return (
    <Box sx={sx}>
      {/* Section Header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.2s',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: expanded ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
          '&:hover': {
            bgcolor: alpha(theme.palette.action.hover, 0.04),
            borderColor: alpha(theme.palette.divider, 0.2),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            sx={{
              p: 0.5,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandIcon fontSize="small" />
          </IconButton>
          
          <Typography variant="body2" fontWeight={500}>
            {title}
          </Typography>
          
          {count > 0 && (
            <Badge
              badgeContent={count}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  position: 'static',
                  transform: 'none',
                  fontSize: 11,
                  height: 18,
                  minWidth: 18,
                },
              }}
            />
          )}
        </Box>
        
        {/* Preview content when collapsed */}
        {!expanded && preview && (
          <Box sx={{ ml: 3.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            {preview}
          </Box>
        )}
      </Box>
      
      {/* Section Content */}
      <Collapse 
        in={expanded} 
        timeout="auto" 
        unmountOnExit
        sx={{
          // Prevent layout shift by reserving space for potential scrollbar
          minHeight: 0,
          '& .MuiCollapse-wrapper': {
            minHeight: 0,
          },
          '& .MuiCollapse-wrapperInner': {
            minHeight: 0,
          }
        }}
      >
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CollapsibleSection;